import { useLazyQuery } from '@apollo/client';
import { useMemo } from 'react';
import { getChargeCodeQuery } from 'graphql-queries';
import { GroupType as GraphQLGroupType } from 'graphql-query-contracts';
import {
  mapGqlChargeCodeToDomainChargeCode,
  ChargeCode as DomainChargeCode,
} from 'domains-billing-web-mappers';

export class GetChargeCodeError extends Error {
  constructor(message: string) {
    super(message);
    this.name = 'GetChargeCodeError';
  }
}

export type UseChargeCodeParams = {
  chargeCodeId?: string;
  groupId?: string;
};

export type UseChargeCodeResult = {
  chargeCode: DomainChargeCode | undefined;
  loading: boolean;
  error: GetChargeCodeError | undefined;
  fetchChargeCode: () => void;
};

export const useChargeCode = ({
  chargeCodeId,
  groupId,
}: UseChargeCodeParams): UseChargeCodeResult => {
  const [fetchChargeCodeQuery, { data, loading, error: queryError }] =
    useLazyQuery(getChargeCodeQuery, {
      fetchPolicy: 'network-only',
    });

  const fetchChargeCode = () => {
    fetchChargeCodeQuery({
      variables: {
        getChargeCodeRequest: {
          id: chargeCodeId,
          groupType: GraphQLGroupType.GroupTypeActivateChannel,
          groupId,
        },
      },
    });
  };

  const processedError = useMemo(() => {
    if (!queryError) return undefined;

    return new GetChargeCodeError(
      queryError.message ||
        "Failed to fetch charge code"
    );
  }, [queryError]);

  const mappedChargeCode = useMemo(() => {
    if (!data?.chargeCode) return undefined;

    return mapGqlChargeCodeToDomainChargeCode(data.chargeCode);
  }, [data]);

  return {
    chargeCode: mappedChargeCode,
    loading,
    error: processedError,
    fetchChargeCode,
  };
};
